// Vehicle Route Tracker trip timeline (converted from .js, ST-00218 lm-status: typed, and each
// trip card shows a "Load status" field — the load-match ledger's label for the trip's dispatch
// ticket, or the receipt's on a receipt-only trip — when load matching is on).
import { useMemo } from 'react';
import { FaArrowRight, FaClock, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { formatMiningTime } from '../../../utils/dateUtils';
import { useLoadStatuses } from '../../../hooks/useLoadStatuses';
import { LoadStatusCell } from '../../common/LoadStatusBadge';
import type { TicketLoadStatus } from '../../../types/loadMatching';
import type { MatchingInfo } from '../../../types/routeFigures';
import type {
  VehicleTrip,
  VehicleTripStatus,
  VehicleTripsPagination,
} from '../../../types/clientPages/vehicleRouteTracker';

const formatTime = (dateStr: string | null): string => {
  if (!dateStr) return '--:--';
  return formatMiningTime(dateStr) || '--:--';
};

const formatWeight = (w: number | null): string => {
  if (w == null) return '-';
  return `${w.toFixed(2)} t`;
};

const formatTransit = (minutes: number | null): string | null => {
  if (minutes == null) return null;
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const formatDayHeader = (dateStr: string): string => {
  // dateStr is YYYY-MM-DD; append noon time to prevent timezone date-shift
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-ZA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const getDateKey = (trip: VehicleTrip): string => {
  const ts = trip.dispatchTimestamp || trip.receiptTimestamp;
  if (!ts) return 'unknown';
  // Extract YYYY-MM-DD directly from timezone-free string
  const tIdx = ts.indexOf('T');
  return tIdx !== -1 ? ts.substring(0, tIdx) : ts;
};

/** A trip with a receipt and no dispatch in the selected dates. */
const isReceiptOnly = (trip: VehicleTrip): boolean =>
  trip.tripStatus === 'orphaned-receipt' || trip.tripStatus === 'unmatched-receipt';

/** The uid whose Load status a trip shows: its dispatch, or the receipt of a receipt-only trip. */
const loadStatusUid = (trip: VehicleTrip): string | null =>
  isReceiptOnly(trip) ? trip.receiptTicketUid || null : trip.dispatchTicketUid || null;

interface StatusStyle {
  border: string;
  bg: string;
  label: string;
  labelClass: string;
}

const statusConfig: Record<VehicleTripStatus, StatusStyle> = {
  paired: {
    border: 'border-l-emerald-400',
    bg: 'bg-surface',
    label: 'Completed',
    labelClass: 'bg-emerald-50 text-emerald-700',
  },
  'in-transit': {
    border: 'border-l-amber-400',
    bg: 'bg-amber-50/30',
    label: 'In Transit',
    labelClass: 'bg-amber-50 text-amber-700',
  },
  'orphaned-receipt': {
    border: 'border-l-red-300',
    bg: 'bg-surface',
    label: 'Unmatched',
    labelClass: 'bg-red-50 text-red-600',
  },
  'unmatched-receipt': {
    border: 'border-l-red-300',
    bg: 'bg-surface',
    label: 'Unmatched',
    labelClass: 'bg-red-50 text-red-600',
  },
};

interface TripCardProps {
  trip: VehicleTrip;
  tripNumber: number;
  /** Load matching is on (useLoadStatuses enabled === true): show the Load status field. */
  showLoadStatus: boolean;
  loadStatus: TicketLoadStatus | undefined;
  loadStatusLoading: boolean;
}

const TripCard = ({
  trip,
  tripNumber,
  showLoadStatus,
  loadStatus,
  loadStatusLoading,
}: TripCardProps) => {
  const isPaired = trip.tripStatus === 'paired';
  const isInTransit = trip.tripStatus === 'in-transit';
  const isOrphaned = isReceiptOnly(trip);
  const config = statusConfig[trip.tripStatus] || statusConfig.paired;

  return (
    <div
      data-testid="trip-card"
      className={`rounded-lg border border-slate-200 border-l-4 ${config.border} ${config.bg} shadow-sm hover:shadow-md transition-shadow`}
    >
      {/* Trip header bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50/80 border-b border-slate-100 rounded-t-lg">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-slate-400">#{tripNumber}</span>
          <span className="text-xs font-semibold text-slate-600">
            {trip.dispatchLocation || trip.actualReceiptLocation}
            {trip.intendedReceiptLocation && (
              <>
                <FaArrowRight className="inline w-2.5 h-2.5 mx-1.5 text-slate-400" />
                {trip.intendedReceiptLocation}
              </>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {trip.transitMinutes != null && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-slate-500 bg-surface border border-slate-200 rounded-full">
              <FaClock className="w-2.5 h-2.5" />
              {formatTransit(trip.transitMinutes)}
            </span>
          )}
          <span
            className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${config.labelClass}`}
          >
            {config.label}
          </span>
        </div>
      </div>

      <div className="px-4 py-3 space-y-0">
        {/* Dispatch row */}
        {trip.dispatchTimestamp && (
          <div className="flex items-center gap-3 py-2">
            {/* Dispatch badge */}
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
              <span className="text-[11px] font-bold text-white">D</span>
            </div>

            {/* Time */}
            <div className="flex-shrink-0 w-12">
              <span className="text-sm font-mono font-bold text-slate-700">
                {formatTime(trip.dispatchTimestamp)}
              </span>
            </div>

            {/* Location & details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">
                  {trip.dispatchLocation}
                </span>
                {trip.dispatchTicketNo && (
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-px rounded">
                    {trip.dispatchTicketNo}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                <span className="font-medium">{formatWeight(trip.dispatchWeight)}</span>
                {trip.product && <span className="truncate">{trip.product}</span>}
                {trip.driver && <span className="text-slate-400 truncate">{trip.driver}</span>}
              </div>
            </div>
          </div>
        )}

        {/* Connecting line between D and R */}
        {trip.dispatchTimestamp && (isPaired || isInTransit) && (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 flex justify-center">
              <div className="w-px h-5 border-l-2 border-dashed border-slate-300" />
            </div>
            {isInTransit && (
              <span className="inline-flex items-center gap-1 ml-12 text-xs font-medium text-amber-600">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                Awaiting receipt...
              </span>
            )}
          </div>
        )}

        {/* Receipt row */}
        {trip.receiptTimestamp && (
          <div className="flex items-center gap-3 py-2">
            {/* Receipt badge */}
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isOrphaned ? 'bg-amber-400' : 'bg-emerald-500'}`}
            >
              <span className="text-[11px] font-bold text-white">R</span>
            </div>

            {/* Time */}
            <div className="flex-shrink-0 w-12">
              <span className="text-sm font-mono font-bold text-slate-700">
                {formatTime(trip.receiptTimestamp)}
              </span>
            </div>

            {/* Location & details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">
                  {trip.actualReceiptLocation}
                </span>
                {trip.receiptTicketNo && (
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-px rounded">
                    {trip.receiptTicketNo}
                  </span>
                )}
                {isOrphaned && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-600">
                    <FaExclamationTriangle className="w-3 h-3" />
                    {trip.tripStatus === 'unmatched-receipt' && trip.receiptDeliveryNo
                      ? `Dispatch ${trip.receiptDeliveryNo} not found in these dates`
                      : 'No matching dispatch'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                <span className="font-medium">{formatWeight(trip.receiptWeight)}</span>
                {trip.variancePercent != null && (
                  <span
                    className={`font-medium ${Math.abs(trip.variancePercent) > 2 ? 'text-red-500' : 'text-slate-400'}`}
                  >
                    {trip.variancePercent > 0 ? '+' : ''}
                    {trip.variancePercent.toFixed(2)}% variance
                  </span>
                )}
                {trip.product && !trip.dispatchTimestamp && (
                  <span className="truncate">{trip.product}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Load status (ST-00218): the ledger's label for this trip's dispatch ticket */}
      {showLoadStatus && (
        <div className="flex items-center gap-2 px-4 py-2 border-t border-slate-100 text-xs">
          <span className="font-medium text-slate-500">Load status</span>
          <LoadStatusCell status={loadStatus} loading={loadStatusLoading} />
        </div>
      )}
    </div>
  );
};

interface TripGroup {
  date: string;
  trips: VehicleTrip[];
}

export interface TripTimelineProps {
  trips: VehicleTrip[] | null | undefined;
  pagination: VehicleTripsPagination | null | undefined;
  onLoadMore: () => void;
  loading: boolean;
  /**
   * ST-00218 P4: how the trips were paired (the trips answer's `matching`, on tenants with
   * "Truck + time" routes). With `fallback` set they were paired by delivery number instead.
   */
  matching?: MatchingInfo | null;
}

/** The small note when the trips fell back to delivery-number pairing (ST-00218 P4). */
const MatchingNote = ({ matching }: { matching?: MatchingInfo | null }) => {
  if (!matching || !matching.fallback || !matching.note) return null;
  return (
    <p
      role="note"
      title={matching.detail || undefined}
      className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
    >
      <FaInfoCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        <span className="font-semibold">{matching.note}</span>
        {matching.detail && <span className="font-normal"> · {matching.detail}</span>}
      </span>
    </p>
  );
};

const TripTimeline = ({ trips, pagination, onLoadMore, loading, matching }: TripTimelineProps) => {
  // Group trips by date
  const groupedTrips = useMemo((): TripGroup[] => {
    if (!trips || trips.length === 0) return [];

    const groups: TripGroup[] = [];
    let currentGroup: TripGroup | null = null;

    for (const trip of trips) {
      const dateKey = getDateKey(trip);
      if (!currentGroup || dateKey !== currentGroup.date) {
        currentGroup = { date: dateKey, trips: [] };
        groups.push(currentGroup);
      }
      currentGroup.trips.push(trip);
    }

    return groups;
  }, [trips]);

  // Load status per trip (hidden unless the server says load matching is on)
  const loadStatusUids = useMemo(
    () => (trips ?? []).map(loadStatusUid).filter((u): u is string => !!u),
    [trips]
  );
  const {
    enabled: loadStatusEnabled,
    statuses: loadStatuses,
    loading: loadStatusLoading,
  } = useLoadStatuses(loadStatusUids);
  const showLoadStatus = loadStatusEnabled === true;

  // Calculate running trip number
  const tripNumberOffset = useMemo(() => {
    if (!pagination) return 0;
    return (pagination.page - 1) * pagination.limit;
  }, [pagination]);

  if (!trips || trips.length === 0) {
    return (
      <div className="bg-surface rounded-xl border border-slate-200 shadow-sm p-8 text-center">
        {matching?.fallback && (
          <div className="mb-4 text-left">
            <MatchingNote matching={matching} />
          </div>
        )}
        <FaClock className="w-8 h-8 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">
          No trips found for this vehicle in the selected date range.
        </p>
      </div>
    );
  }

  const hasMore = pagination && pagination.page < pagination.totalPages;

  let runningIndex = 0;

  return (
    <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Trip Timeline</h3>
          {pagination && (
            <span className="text-xs text-slate-400">
              Showing {trips.length} of {pagination.totalTrips.toLocaleString()} trips
            </span>
          )}
        </div>
        {matching?.fallback && (
          <div className="mt-3">
            <MatchingNote matching={matching} />
          </div>
        )}
      </div>

      <div className="px-5 py-4 max-h-[700px] overflow-y-auto space-y-6">
        {groupedTrips.map((group) => (
          <div key={group.date}>
            {/* Day header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                {formatDayHeader(group.date)}
              </span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            {/* Trip cards for this day */}
            <div className="space-y-3">
              {group.trips.map((trip, idx) => {
                runningIndex++;
                const uid = loadStatusUid(trip);
                return (
                  <TripCard
                    key={`${trip.dispatchTicketNo || ''}-${trip.receiptTicketNo || ''}-${idx}`}
                    trip={trip}
                    tripNumber={tripNumberOffset + runningIndex}
                    showLoadStatus={showLoadStatus}
                    loadStatus={uid ? loadStatuses[uid] : undefined}
                    loadStatusLoading={loadStatusLoading}
                  />
                );
              })}
            </div>
          </div>
        ))}

        {hasMore && (
          <div className="text-center pt-2 pb-1">
            <button
              onClick={onLoadMore}
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  Loading...
                </>
              ) : (
                <>Load More Trips</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TripTimeline;
